import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {SurfaceDnDContext, SurfacePoint} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {CSSProperties} from 'react';
import {DraggableCore, DraggableData} from 'react-draggable';

const styles = require('./SurfaceDnDGlass.less');
const cx = classnames.bind(styles);

@observer
export class SurfaceDnDGlass extends React.Component<any, any>
{
    private _glassRef:HTMLElement;
    private _draggableRef:DraggableCore;

    @observable
    private _clientRect:ClientRect = null;

    @observable
    private _boxWidth:number = null;

    @observable
    private _boxHeight:number = null;

    @observable
    private _curPoint:SurfacePoint = null;

    @bind
    private _onGlassRef(ref:HTMLElement)
    {
        this._glassRef = ref;
    }

    @bind
    private _onDraggableRef(draggableRef:any)
    {
        const {surfaceDnD} = commonControllers;

        this._draggableRef = draggableRef;

        if( draggableRef )
            surfaceDnD.processDragSource(this._curPoint, this._draggableRef);
    }

    @action.bound
    private _onDrag(event:MouseEvent, data:DraggableData):void
    {
        if( !this._curPoint )
            return;

        const newX = this._curPoint.x + data.deltaX;
        const newY = this._curPoint.y + data.deltaY;

        this._curPoint = new SurfacePoint(newX, newY);

        const {surfaceDnD} = commonControllers;

        surfaceDnD.processDropTarget(event, data, false);
    }

    @action.bound
    private _onDragStop(event:MouseEvent, data:DraggableData):void
    {
        const {ui: {visualDesigner: {surfaceEditor: {area: {surfaceDnDState}}}}} = rootState;
        const {surfaceDnD} = commonControllers;

        surfaceDnD.processDropTarget(event, data, true);

        surfaceDnDState.dragContext = null;
        this._curPoint = null;
    }

    @action
    private _computeInitialPositionOnGlass(dragContext:SurfaceDnDContext):SurfacePoint
    {
        const {boxUniqueId} = dragContext;

        const glassClientRect:ClientRect = this._glassRef.getBoundingClientRect();
        const boxEl:HTMLElement = document.querySelector(`[data-box-unique-id="${boxUniqueId}"]`);

        if( !boxEl )
        {
            this._clientRect = {
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                width: 0,
                height: 0
            };
            this._boxWidth = 0;
            this._boxHeight = 0;

            return new SurfacePoint(0, -glassClientRect.top);
        }

        this._clientRect = boxEl.getBoundingClientRect();
        this._boxWidth = boxEl.clientWidth;
        this._boxHeight = boxEl.clientHeight;

        const x:number = Math.max(0, this._clientRect.left);
        const y:number = Math.max(0, this._clientRect.top);

        return new SurfacePoint(x, y - glassClientRect.top);
    }

    @computed
    private get _boxStyle():React.CSSProperties | null
    {
        const {ui: {visualDesigner: {surfaceEditor: {area: {zoom, surfaceDnDState: {dragContext}}}}}} = rootState;

        if( dragContext && dragContext )
        {
            let style:CSSProperties = {
                transform: `translate(${(this._clientRect.width - this._boxWidth * zoom) / 2}px, ${(this._clientRect.height - this._boxHeight * zoom) / 2}px)`,
                width: `${this._boxWidth * zoom}px`,
                height: `${this._boxHeight * zoom}px`
            };

            if( dragContext.dnd.isCustomPreviewStyle && dragContext.dnd.isCustomPreviewStyle.value )
            {
                style = {
                    ...style,
                    backgroundImage: 'none',
                    backgroundSize: 'auto',
                    ...(dragContext.dnd.previewBackground && dragContext.dnd.previewBackground.toCss()),
                    ...(dragContext.dnd.previewBorder && dragContext.dnd.previewBorder.toCSS()),
                    boxShadow: dragContext.dnd.previewBoxShadow && dragContext.dnd.previewBoxShadow.toCssString()
                };
            }

            if( dragContext.rotation
                && dragContext.rotation.isEnabled && dragContext.rotation.isEnabled.value
                && dragContext.rotation.angle )
                style.transform = `${style.transform} rotate(${dragContext.rotation.angle.value}deg)`;

            return style;
        }

        return null;
    }

    private _renderDragItem():JSX.Element
    {
        const {ui: {visualDesigner: {surfaceEditor: {area: {surfaceDnDState}}}}} = rootState;
        const dragContext:SurfaceDnDContext = surfaceDnDState.dragContext;

        if( !dragContext )
            return null;

        if( !this._curPoint )
            this._curPoint = this._computeInitialPositionOnGlass(dragContext);

        const wrapperStyle = {
            transform: `translate3d(${this._curPoint.x}px, ${this._curPoint.y}px, 0)`,
            width: this._clientRect.width,
            height: this._clientRect.height
        };

        return (
            <DraggableCore ref={this._onDraggableRef}
                           onDrag={this._onDrag}
                           onStop={this._onDragStop}
                           enableUserSelectHack={false}
            >
                <div className={styles.dragPreviewWrapper} style={wrapperStyle}>
                    <div className={styles.dragPreview} style={this._boxStyle} />
                </div>
            </DraggableCore>
        );
    }

    public render():JSX.Element
    {
        return (
            <Row aRef={this._onGlassRef} className={cx('surfaceDnDGlass')}>
                {this._renderDragItem()}
            </Row>
        );
    }
}
